/**
 * allSourcesService
 *
 * Data-fetching layer for the "All Sources" page. Mirrors journalArticlesService
 * but targets the GET /api/biblio/sources endpoint, which returns biblio sources
 * of every sourceType (article, report, thesis, etc.) rather than only articles.
 *
 * The record shape returned by the sources endpoint is identical to a journal
 * article (they are the same DynamoDB table rows), so this module reuses the
 * JournalArticle type and the paginated response shape from journalArticlesService.
 */

import { JournalArticle } from "@/services/journalArticlesService"

/**
 * Response shape from the paginated all-sources API endpoint
 * (GET /api/biblio/sources).
 */
export interface AllSourcesResponse {
  /** The page of source records (newest first). */
  items: JournalArticle[]
  /**
   * Opaque cursor for fetching the next page, or null when the current page is
   * the last one. Pass this value back as the `cursor` argument to getAllSources
   * to retrieve the following page.
   */
  nextCursor: string | null
  /** Whether a further page exists (equivalently, nextCursor !== null). */
  hasMore: boolean
  /** Number of items per page. */
  pageSize: number
  /** The sourceType filter applied, or null when all types are returned. */
  sourceType: string | null
  /** The search term filter applied, or null. */
  searchTerm: string | null
}

// Cached API base URL loaded from aws-exports.json.
let ALL_SOURCES_API_URL = ""

/**
 * Load the all-sources API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/biblio/sources path for local development when
 * no explicit API URL is configured. Mirrors the loader in journalArticlesService
 * so the two endpoints resolve their base URL identically.
 *
 * @returns The full URL for the all-sources API endpoint.
 * @throws Error if the API URL cannot be resolved from configuration.
 */
async function loadApiUrl(): Promise<string> {
  if (ALL_SOURCES_API_URL) {
    return ALL_SOURCES_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    ALL_SOURCES_API_URL = baseUrl ? `${baseUrl}/api/biblio/sources` : "/api/biblio/sources"
    return ALL_SOURCES_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("sources API URL not configured")
  }
}

/**
 * Optional filter parameters for the all-sources API.
 *
 * These mirror the subset of journal-articles filters that make sense for a
 * source-type-agnostic listing, plus the all-sources-specific `sourceType`
 * filter used by the table toolbar.
 */
export interface AllSourcesFilters {
  /**
   * Single source type slug to scope results to (e.g. "report", "thesis").
   * When omitted, sources of every type are returned. Valid values match the
   * backend ALL_SOURCE_TYPES list / the frontend sourceTypeLabelMap keys.
   */
  sourceType?: string
  /**
   * Case-insensitive search term matched across sourceId, title, citation,
   * doi, and pubmedId.
   */
  searchTerm?: string
}

/**
 * Fetch a page of biblio sources (all source types) from the
 * GET /api/biblio/sources endpoint using cursor-based (keyset) pagination.
 *
 * Supports optional filtering by a single sourceType and/or a search term.
 * Results are always sorted by createdDate descending (newest first). Pages are
 * fetched forward by passing back the `nextCursor` from the previous response;
 * there is no random page access or total count (inherent to keyset
 * pagination).
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param cursor - Opaque pagination cursor from a previous response's
 *   nextCursor. Pass null/undefined for the first page.
 * @param pageSize - Number of items per page (default 50, max 200).
 * @param filters - Optional filters: sourceType and/or searchTerm.
 * @returns Response with items, nextCursor, hasMore, and the applied filters.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getAllSources(
  idToken: string,
  cursor: string | null = null,
  pageSize: number = 50,
  filters?: AllSourcesFilters
): Promise<AllSourcesResponse> {
  const apiUrl = await loadApiUrl()

  // Build query string with the pagination cursor and optional filters.
  const params = new URLSearchParams()
  params.set("pageSize", String(pageSize))

  if (cursor) {
    params.set("cursor", cursor)
  }
  if (filters?.sourceType) {
    params.set("sourceType", filters.sourceType)
  }
  if (filters?.searchTerm) {
    params.set("searchTerm", filters.searchTerm)
  }

  const response = await fetch(`${apiUrl}?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data: AllSourcesResponse = await response.json()
  return data
}

/**
 * Fetch a single biblio source by its source ID (biblio ID), regardless of
 * source type.
 *
 * Uses the sources endpoint with a searchTerm set to the numeric sourceId.
 * The backend routes a purely-numeric searchTerm to an exact sourceId lookup
 * (a GetItem on the table's partition key), returning at most one item.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param sourceId - The numeric source (biblio) ID of the source.
 * @returns The matching source record (typed as JournalArticle).
 * @throws Error if the source is not found or the API call fails.
 */
export async function getSourceById(
  idToken: string,
  sourceId: number
): Promise<JournalArticle> {
  // No cursor (first/only page), page size 1; the numeric searchTerm triggers
  // the backend's exact sourceId lookup branch.
  const response = await getAllSources(idToken, null, 1, {
    searchTerm: String(sourceId),
  })

  const source = response.items.find((s) => s.sourceId === sourceId)
  if (!source) {
    throw new Error(`Source with source ID ${sourceId} not found`)
  }

  return source
}
